import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

interface Props {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  maxWidth?: string
}

export default function Modal({
  open,
  onClose,
  title,
  description,
  children,
  maxWidth = 'max-w-lg',
}: Props) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  // Portal a document.body: evita que un ancestro con transform/filter/
  // backdrop-filter "atrape" el position:fixed y deje el modal por debajo
  // de otros controles.
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4">
      <div className={`w-full ${maxWidth} surface-deep p-6 animate-slideUp`}>
        <div className="flex items-start justify-between mb-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold t-primary">{title}</h2>
            {description && <p className="text-xs t-muted mt-1">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-3 -mt-1 -mr-1 w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}
